import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';
import { NavLink } from 'react-router-dom';

import { EmployerNavLink } from './components/employers/EmployerNavLink';
import { EnvelopeNavLinks } from './components/envelopes/EnvelopeNavLinks';
import { SidebarHeaderDocument } from './components/header/graphql/query.sidebarHeader.generated';
import { SidebarHeader } from './components/header/SidebarHeader';
import { NavBackgroundScrim } from './components/NavBackgroundScrim';
import { OnboardingOptionNavLinks } from './components/onboarding-options/OnboardingOptionNavLinks';
import { RedirectNavLinks } from './components/redirect-links/RedirectNavLinks';
import { isSidebarVisible } from './hooks/isSidebarVisible';
import { useOutsideClickRef } from './hooks/useOutsideClickRef';
import { useProcessNavTransition } from './hooks/useProcessNavTransition';
import { useExpandSidebar, useSidebarMode } from './hooks/useSidebarMode';

import { Nav } from '../Shared/components-v2/nav/nav';
import { navItemStyles } from '../Shared/components-v2/nav/nav-link-styles';
import NavHeader from '../Shared/components-v2/nav-header/nav-header';
import { urlBuilder } from '../Shared/urlBuilder';

const navCss = {
  height: '100%',
  width: '100%',
  position: 'fixed',
  top: 0,
  overflow: 'auto',
  pb: 'space.300',
  md: { width: '240px' },
};

export default function Sidebar() {
  const [sidebarMode] = useSidebarMode();
  const expandSidebar = useExpandSidebar();
  const outsideClickRef = useOutsideClickRef();
  const processNavTransition = useProcessNavTransition();
  const isVisible = isSidebarVisible(sidebarMode);

  const { loading, data } = useGraphQLQuery(SidebarHeaderDocument, {
    fetchPolicy: 'cache-first',
    throwOnError: false,
  });

  const advisor = data?.advisor;
  const hasLogo = !loading && !advisor;

  return (
    <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
      {(sidebarMode === 'expanded' || sidebarMode === 'collapsed') && (
        <NavHeader hasLogo={hasLogo} onHamburgerClick={expandSidebar} />
      )}

      {sidebarMode === 'expanded' && <NavBackgroundScrim />}

      <Box ref={outsideClickRef}>
        <Nav css={{ ...navCss, display: isVisible ? 'block' : 'none' }}>
          <SidebarHeader loading={loading} advisor={advisor} />
          <NavLink to={urlBuilder.appHome()} onClick={processNavTransition} className={navItemStyles()}>
            <Nav.Icon name="Home" />
            <Nav.Text>Home</Nav.Text>
          </NavLink>
          <OnboardingOptionNavLinks />
          <EnvelopeNavLinks />
          <Nav.LinkButton to={urlBuilder.addGoal.absolute()}>Open new account</Nav.LinkButton>
          <Nav.Separator />
          <EmployerNavLink />
          <RedirectNavLinks />
        </Nav>
      </Box>
    </ErrorBoundary>
  );
}
